import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, FREQUENCIES, JOB_TEMPLATES, PRIORITIES } from "@/lib/constants";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TaskPlanEditor, { cleanPlan, planTotal } from "@/components/jobs/TaskPlanEditor";

const field = "rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11";
const EMPTY = { title: "", category: "VAT", frequency: "quarterly", duration_minutes: 120, lead_days: 14, priority: "medium", first_due: "", active: true, notes: "" };

function Pick({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={field} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Templates({ onPick }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="job-templates">
      {JOB_TEMPLATES.map((t) => (
        <button type="button" key={t.key} onClick={() => onPick(t)} data-testid={`job-template-${t.key}`}
          className="rounded-full border border-[#103080]/20 px-3 py-1.5 text-xs font-semibold text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200">
          {t.title}
        </button>
      ))}
    </div>
  );
}

export default function JobDialog({ job, clientId, directory, templates, roles, defaultAssignee, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const editing = Boolean(job?.id);

  useEffect(() => {
    if (job) setForm({ ...EMPTY, assignee_id: defaultAssignee, ...job });
  }, [job, defaultAssignee]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const pickTemplate = ({ key, ...t }) => setForm((f) => ({ ...f, ...t }));

  const save = async (e) => {
    e.preventDefault();
    const plan = form.task_plan || [];
    const body = {
      client_id: clientId, title: form.title, category: form.category, frequency: form.frequency, priority: form.priority,
      duration_minutes: plan.length ? planTotal(plan) : Number(form.duration_minutes), lead_days: Number(form.lead_days), first_due: form.first_due,
      assignee_id: form.assignee_id, active: form.active, notes: form.notes, template_id: form.template_id || null, task_plan: cleanPlan(plan),
    };
    try {
      if (editing) await api.patch(`/jobs/${job.id}`, body);
      else await api.post("/jobs", body);
      toast.success(editing ? "Recurring job updated" : "Recurring job created. It will appear in the planner ahead of each deadline.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this recurring job? Open tasks it created will be removed; completed ones are kept.")) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Recurring job deleted");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={Boolean(job)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="job-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">{editing ? "Edit recurring job" : "New recurring job"}</DialogTitle>
          <DialogDescription>Each due date becomes a task in the assignee's planner, starting the set number of days before.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {!editing && <Templates onPick={pickTemplate} />}
          <div className="space-y-1.5">
            <Label>Job</Label>
            <Input required value={form.title} onChange={set("title")} className={field} placeholder="e.g. VAT return" data-testid="job-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Pick value={form.category} onChange={set("category")} testId="job-category-select" options={Object.keys(CATEGORIES).map((c) => ({ value: c, label: c }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Repeats</Label>
              <Pick value={form.frequency} onChange={set("frequency")} testId="job-frequency-select" options={FREQUENCIES} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>First due date</Label>
              <Input type="date" required value={form.first_due} onChange={set("first_due")} className={field} data-testid="job-first-due-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Start work (days before)</Label>
              <Input type="number" min={0} max={365} required value={form.lead_days} onChange={set("lead_days")} className={field} data-testid="job-lead-days-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Time needed (minutes)</Label>
              <Input type="number" min={5} step={5} required value={form.duration_minutes} onChange={set("duration_minutes")} className={field} data-testid="job-duration-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Pick value={form.priority} onChange={set("priority")} testId="job-priority-select" options={PRIORITIES} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Assigned to</Label>
            <Pick value={form.assignee_id || ""} onChange={set("assignee_id")} testId="job-assignee-select" options={directory.map((p) => ({ value: p.id, label: p.name }))} />
          </div>
          <div className="grid">
            <TaskPlanEditor templates={templates} roles={roles} directory={directory} defaultAssignee={form.assignee_id || defaultAssignee}
              value={{ template_id: form.template_id, task_plan: form.task_plan || [] }} onChange={(v) => setForm((f) => ({ ...f, ...v }))} testPrefix="job" />
          </div>
          {editing && (
            <label className="flex items-center gap-3 text-sm text-[#103080]">
              <Switch checked={form.active} onCheckedChange={set("active")} data-testid="job-active-switch" />
              {form.active ? "Active — creating tasks" : "Paused — no new tasks"}
            </label>
          )}
          <div className="flex items-center justify-between pt-2">
            {editing ? (
              <button type="button" onClick={remove} data-testid="job-delete-button" className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors duration-200">
                <Trash2 size={15} /> Delete
              </button>
            ) : <span />}
            <button type="submit" data-testid="job-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">
              {editing ? "Save changes" : "Create job"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
